/**
 * My Appeals Panel
 * Shows contractor/verifier/inspector their filed appeals and status
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Gavel,
  Schedule,
  CheckCircle,
  Cancel,
  Add,
  Send,
  ExpandMore,
  AttachFile,
  Message
} from '@mui/icons-material';
import reviewAppealService from '../../services/reviewAppealService';
import { formatDate, formatRelativeTime } from '../../utils/helpers';
import DialogCloseButton from '../common/DialogCloseButton';

const MyAppealsPanel = () => {
  const [appeals, setAppeals] = useState([]);
  const [filteredAppeals, setFilteredAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState('all');
  const [selectedAppeal, setSelectedAppeal] = useState(null);
  const [evidenceDialog, setEvidenceDialog] = useState(false);
  const [messageDialog, setMessageDialog] = useState(false);
  const [evidenceData, setEvidenceData] = useState({ type: '', fileUrl: '', description: '' });
  const [messageText, setMessageText] = useState('');
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [withdrawAppealId, setWithdrawAppealId] = useState(null);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadAppeals();
  }, []);

  useEffect(() => {
    filterAppeals();
  }, [tabValue, appeals]);

  const loadAppeals = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await reviewAppealService.getMyAppeals();
      
      if (result.success) {
        setAppeals(result.data || []);
      }
    } catch (error) {
      console.error('Error loading appeals:', error);
      setError(error.message || 'Failed to load appeals');
    } finally {
      setLoading(false);
    }
  };

  const filterAppeals = () => {
    if (tabValue === 'all') {
      setFilteredAppeals(appeals);
    } else {
      setFilteredAppeals(appeals.filter(a => a.status === tabValue));
    }
  };

  const handleAddEvidence = async () => {
    if (!evidenceData.type || !evidenceData.description) {
      return;
    }

    try {
      setSubmitting(true);
      await reviewAppealService.addEvidence(selectedAppeal.id, {
        evidenceType: evidenceData.type,
        fileUrl: evidenceData.fileUrl,
        description: evidenceData.description
      });
      
      // Refresh appeals
      await loadAppeals();
      
      // Close dialog
      setEvidenceDialog(false);
      setEvidenceData({ type: '', fileUrl: '', description: '' });
    } catch (error) {
      console.error('Error adding evidence:', error);
      alert(error.message || 'Failed to add evidence');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) {
      return;
    }

    try {
      setSubmitting(true);
      await reviewAppealService.sendMessage(selectedAppeal.id, messageText.trim());
      
      // Refresh appeals
      await loadAppeals();
      
      // Close dialog
      setMessageDialog(false);
      setMessageText('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert(error.message || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawAppeal = async () => {
    if (!withdrawReason.trim()) {
      return;
    }

    try {
      setSubmitting(true);
      await reviewAppealService.withdrawAppeal(withdrawAppealId, withdrawReason.trim());
      await loadAppeals();
      setWithdrawDialog(false);
      setWithdrawAppealId(null);
      setWithdrawReason('');
    } catch (error) {
      console.error('Error withdrawing appeal:', error);
      alert(error.message || 'Failed to withdraw appeal');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'under_review': return 'info';
      case 'resolved': return 'success';
      case 'withdrawn': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Schedule />;
      case 'under_review': return <Gavel />;
      case 'resolved': return <CheckCircle />;
      case 'withdrawn': return <Cancel />;
      default: return null;
    }
  };

  const evidenceTypes = [
    { value: 'completion_certificate', label: 'Completion Certificate' },
    { value: 'contract', label: 'Contract/Agreement' },
    { value: 'photo', label: 'Photos/Documentation' },
    { value: 'communication_log', label: 'Messages/Communication' },
    { value: 'payment_proof', label: 'Payment Receipt' },
    { value: 'witness_statement', label: 'Witness Statement' },
    { value: 'other', label: 'Other' }
  ];

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading your appeals...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label={`All (${appeals.length})`} value="all" />
          <Tab 
            label={`Pending (${appeals.filter(a => a.status === 'pending').length})`} 
            value="pending" 
          />
          <Tab 
            label={`Under Review (${appeals.filter(a => a.status === 'under_review').length})`} 
            value="under_review" 
          />
          <Tab 
            label={`Resolved (${appeals.filter(a => a.status === 'resolved').length})`} 
            value="resolved" 
          />
        </Tabs>
      </Box>

      {filteredAppeals.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Gavel sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No appeals {tabValue !== 'all' ? tabValue : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tabValue === 'all' 
              ? "You haven't filed any appeals yet." 
              : `You don't have any ${tabValue} appeals.`}
          </Typography>
        </Box>
      ) : (
        filteredAppeals.map((appeal) => (
          <Card key={appeal.id} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      label={appeal.status.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(appeal.status)}
                      icon={getStatusIcon(appeal.status)}
                      size="small"
                    />
                    <Chip
                      label={appeal.appeal_reason.replace('_', ' ')}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Filed {formatRelativeTime(appeal.created_at)}
                    {appeal.sla_deadline && appeal.status === 'pending' && (
                      <> • SLA Deadline: {formatDate(appeal.sla_deadline)}</>
                    )}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {appeal.status === 'pending' && (
                    <>
                      <Button
                        size="small"
                        startIcon={<AttachFile />}
                        onClick={() => {
                          setSelectedAppeal(appeal);
                          setEvidenceDialog(true);
                        }}
                      >
                        Add Evidence
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Message />}
                        onClick={() => {
                          setSelectedAppeal(appeal);
                          setMessageDialog(true);
                        }}
                      >
                        Send Message
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          setWithdrawAppealId(appeal.id);
                          setWithdrawDialog(true);
                        }}
                      >
                        Withdraw
                      </Button>
                    </>
                  )}
                </Box>
              </Box>

              <Typography variant="body1" paragraph sx={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}>
                {appeal.appeal_description}
              </Typography>

              {/* Resolution Info */}
              {appeal.status === 'resolved' && appeal.resolution_type && (
                <Alert 
                  severity={appeal.resolution_type.includes('upheld') ? 'success' : 'error'}
                  sx={{ mt: 2 }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Resolution: {appeal.resolution_type.replace(/_/g, ' ').toUpperCase()}
                  </Typography>
                  {appeal.resolution_notes && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {appeal.resolution_notes}
                    </Typography>
                  )}
                  {appeal.review_removed && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: 'success.main' }}>
                      ✓ Review has been removed from public view
                    </Typography>
                  )}
                </Alert>
              )}

              {/* Evidence and Messages Accordion */}
              <Box sx={{ mt: 2 }}>
                {appeal.evidence && appeal.evidence.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography>
                        <AttachFile sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        Evidence ({appeal.evidence.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {appeal.evidence.map((ev, idx) => (
                          <React.Fragment key={ev.id}>
                            <ListItem>
                              <ListItemText
                                primary={
                                  <Chip label={ev.evidence_type} size="small" sx={{ mr: 1 }} />
                                }
                                secondary={ev.description}
                              />
                            </ListItem>
                            {idx < appeal.evidence.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}

                {appeal.messages && appeal.messages.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography>
                        <Message sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        Messages ({appeal.messages.filter(m => !m.is_internal).length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {appeal.messages.filter(m => !m.is_internal).map((msg, idx) => (
                          <React.Fragment key={msg.id}>
                            <ListItem>
                              <ListItemText
                                primary={msg.sender_name || 'You'}
                                secondary={
                                  <>
                                    <Typography variant="body2" component="span">
                                      {msg.message}
                                    </Typography>
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      {formatRelativeTime(msg.created_at)}
                                    </Typography>
                                  </>
                                }
                              />
                            </ListItem>
                            {idx < appeal.messages.filter(m => !m.is_internal).length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            </CardContent>
          </Card>
        ))
      )}

      {/* Add Evidence Dialog */}
      <Dialog
        open={evidenceDialog}
        onClose={() => !submitting && setEvidenceDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => !submitting && setEvidenceDialog(false)} />
        <DialogTitle>Add Evidence</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              select
              fullWidth
              label="Evidence Type"
              value={evidenceData.type}
              onChange={(e) => setEvidenceData({ ...evidenceData, type: e.target.value })}
              disabled={submitting}
              sx={{ mb: 2 }}
              SelectProps={{
                native: true
              }}
            >
              <option value="">Select type...</option>
              {evidenceTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="File URL (optional)"
              placeholder="https://example.com/evidence.pdf"
              value={evidenceData.fileUrl}
              onChange={(e) => setEvidenceData({ ...evidenceData, fileUrl: e.target.value })}
              disabled={submitting}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              placeholder="Describe this piece of evidence..."
              value={evidenceData.description}
              onChange={(e) => setEvidenceData({ ...evidenceData, description: e.target.value })}
              disabled={submitting}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvidenceDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleAddEvidence}
            disabled={submitting || !evidenceData.type || !evidenceData.description}
            variant="contained"
          >
            {submitting ? 'Adding...' : 'Add Evidence'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Withdraw Appeal Dialog */}
      <Dialog
        open={withdrawDialog}
        onClose={() => !submitting && setWithdrawDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => !submitting && setWithdrawDialog(false)} />
        <DialogTitle>Withdraw Appeal</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The original review will remain unchanged.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for withdrawal"
            placeholder="Please explain why you are withdrawing this appeal..."
            value={withdrawReason}
            onChange={(e) => setWithdrawReason(e.target.value)}
            disabled={submitting}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleWithdrawAppeal}
            disabled={submitting || !withdrawReason.trim()}
            variant="contained"
            color="error"
          >
            {submitting ? 'Withdrawing...' : 'Withdraw Appeal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Message Dialog */}
      <Dialog
        open={messageDialog}
        onClose={() => !submitting && setMessageDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => !submitting && setMessageDialog(false)} />
        <DialogTitle>Send Message</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send a message to the moderation team regarding your appeal.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Type your message here..."
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSendMessage}
            disabled={submitting || !messageText.trim()}
            variant="contained"
            startIcon={<Send />}
          >
            {submitting ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyAppealsPanel;

